import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import { XMarkIcon, PlayIcon, PauseIcon, ClockIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useQuery } from '@tanstack/react-query';
import { gpsApi, GPSDevice } from '@/api/gpsApi';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default markers in React Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface GPSPoint {
  longitude: number;
  latitude: number;
  timestamp: number;
  timestampISO: string;
  speed: number;
  course: number;
}

interface VehiclePlaybackModalProps {
  isOpen: boolean;
  onClose: () => void;
  device: GPSDevice;
}

const VehiclePlaybackModal: React.FC<VehiclePlaybackModalProps> = ({
  isOpen,
  onClose,
  device
}) => {
  const [dateRange, setDateRange] = useState(() => {
    const now = new Date();
    const twoHoursAgo = new Date(now.getTime() - 2 * 60 * 60 * 1000);
    return {
      startDate: twoHoursAgo.toISOString().split('T')[0],
      startTime: twoHoursAgo.toTimeString().split(' ')[0].substring(0, 5),
      endDate: now.toISOString().split('T')[0],
      endTime: now.toTimeString().split(' ')[0].substring(0, 5)
    };
  });
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentPointIndex, setCurrentPointIndex] = useState(0);
  const [playSpeed, setPlaySpeed] = useState(1);
  const intervalRef = useRef<NodeJS.Timeout>();

  const begintime = Math.floor(new Date(`${dateRange.startDate}T${dateRange.startTime}`).getTime() / 1000);
  const endtime = Math.floor(new Date(`${dateRange.endDate}T${dateRange.endTime}`).getTime() / 1000);

  // Fetch GPS playback data
  const { data: playbackData, isLoading: playbackLoading, error: playbackError, refetch } = useQuery({
    queryKey: ['gps-playback', device.imei, begintime, endtime],
    queryFn: () => gpsApi.getDevicePlayback(device.imei, begintime, endtime),
    enabled: isOpen && begintime > 0 && endtime > begintime,
    retry: 1
  });

  const gpsPoints: GPSPoint[] = playbackData?.data?.data?.gpsPoints || [];

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    if (isPlaying && gpsPoints.length > 0) {
      intervalRef.current = setInterval(() => {
        setCurrentPointIndex(prev => {
          if (prev >= gpsPoints.length - 1) {
            setIsPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, 1000 / playSpeed); // Adjust speed
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPlaying, gpsPoints.length, playSpeed]);

  const handlePlayPause = () => {
    if (currentPointIndex >= gpsPoints.length - 1) {
      setCurrentPointIndex(0);
    }
    setIsPlaying(!isPlaying);
  };

  const handleReset = () => {
    setIsPlaying(false);
    setCurrentPointIndex(0);
  };

  const handleFetchPlayback = () => {
    setCurrentPointIndex(0);
    setIsPlaying(false);
    refetch();
  };

  if (!isOpen) return null;

  const currentPoint = gpsPoints[currentPointIndex];
  const pathPoints: [number, number][] = gpsPoints
    .slice(0, currentPointIndex + 1)
    .map(point => [point.latitude, point.longitude]);

  const center: [number, number] = currentPoint 
    ? [currentPoint.latitude, currentPoint.longitude]
    : gpsPoints.length > 0 
    ? [gpsPoints[0].latitude, gpsPoints[0].longitude]
    : [-1.2921, 36.8219]; // Default to Nairobi

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              GPS Playback: {device.deviceName}
            </h2>
            <p className="text-sm text-gray-600">IMEI: {device.imei}</p>
          </div>
          <Button onClick={onClose} variant="ghost" size="sm">
            <XMarkIcon className="w-5 h-5" />
          </Button>
        </div>

        {/* Time Range Selection */}
        <div className="p-6 border-b border-gray-200 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange(prev => ({...prev, startDate: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
              <input
                type="time"
                value={dateRange.startTime}
                onChange={(e) => setDateRange(prev => ({...prev, startTime: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange(prev => ({...prev, endDate: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
              <input
                type="time"
                value={dateRange.endTime}
                onChange={(e) => setDateRange(prev => ({...prev, endTime: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
          </div>
          <div className="mt-4 flex justify-between items-center">
            <Button 
              onClick={handleFetchPlayback}
              disabled={playbackLoading}
              variant="primary"
              size="sm"
            >
              {playbackLoading ? <LoadingSpinner size="sm" /> : <ClockIcon className="w-4 h-4 mr-2" />}
              Load Playback Data
            </Button>
            
            {gpsPoints.length > 0 && (
              <div className="text-sm text-gray-600">
                Found {gpsPoints.length} GPS points
              </div>
            )}
          </div>
        </div>

        {/* Map and Controls */}
        <div className="p-6">
          {playbackError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              Failed to load playback data: {playbackError.toString()}
            </div>
          )}

          {gpsPoints.length > 0 ? (
            <>
              {/* Playback Controls */}
              <div className="mb-4 flex items-center justify-between bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center space-x-4">
                  <Button
                    onClick={handlePlayPause}
                    variant="primary"
                    size="sm"
                  >
                    {isPlaying ? <PauseIcon className="w-4 h-4" /> : <PlayIcon className="w-4 h-4" />}
                    {isPlaying ? 'Pause' : 'Play'}
                  </Button>
                  
                  <Button
                    onClick={handleReset}
                    variant="secondary"
                    size="sm"
                  >
                    Reset
                  </Button>
                  
                  <div className="flex items-center space-x-2">
                    <label className="text-sm text-gray-700">Speed:</label>
                    <select
                      value={playSpeed}
                      onChange={(e) => setPlaySpeed(Number(e.target.value))}
                      className="px-2 py-1 border border-gray-300 rounded text-sm"
                    >
                      <option value={0.5}>0.5x</option>
                      <option value={1}>1x</option>
                      <option value={2}>2x</option>
                      <option value={5}>5x</option>
                      <option value={10}>10x</option>
                    </select>
                  </div>
                </div>
                
                <div className="text-sm text-gray-600">
                  Point {currentPointIndex + 1} of {gpsPoints.length}
                  {currentPoint && (
                    <span className="ml-2">
                      {new Date(currentPoint.timestamp * 1000).toLocaleString()}
                    </span>
                  )}
                </div>
              </div>

              {/* Enhanced Progress Bar with Speed Visualization */}
              <div className="mb-4">
                <div className="w-full bg-gray-200 rounded-full h-3 relative overflow-hidden">
                  <div 
                    className="h-3 rounded-full transition-all duration-300"
                    style={{ 
                      width: `${((currentPointIndex + 1) / gpsPoints.length) * 100}%`,
                      background: currentPoint 
                        ? `linear-gradient(90deg, 
                            ${currentPoint.speed > 50 ? '#DC2626' : currentPoint.speed > 20 ? '#F59E0B' : '#10B981'}, 
                            ${currentPoint.speed > 50 ? '#EF4444' : currentPoint.speed > 20 ? '#FBBF24' : '#34D399'}
                          )`
                        : '#2563EB'
                    }}
                  ></div>
                  {currentPoint && (
                    <div 
                      className="absolute top-0 h-3 w-1 bg-white shadow-lg"
                      style={{ left: `${((currentPointIndex + 1) / gpsPoints.length) * 100}%` }}
                    ></div>
                  )}
                </div>
                
                {/* Speed indicator below progress bar */}
                <div className="flex justify-between text-xs text-gray-500 mt-1 mb-2">
                  <span>Start: {new Date(gpsPoints[0]?.timestamp * 1000).toLocaleTimeString()}</span>
                  {currentPoint && (
                    <span className={`font-semibold ${currentPoint.speed > 50 ? 'text-red-600' : currentPoint.speed > 20 ? 'text-yellow-600' : 'text-green-600'}`}>
                      {currentPoint.speed} km/h
                    </span>
                  )}
                  <span>End: {new Date(gpsPoints[gpsPoints.length - 1]?.timestamp * 1000).toLocaleTimeString()}</span>
                </div>
                
                <input
                  type="range"
                  min={0}
                  max={gpsPoints.length - 1}
                  value={currentPointIndex}
                  onChange={(e) => {
                    setCurrentPointIndex(Number(e.target.value));
                    setIsPlaying(false);
                  }}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                  style={{
                    background: `linear-gradient(to right, #2563EB 0%, #2563EB ${((currentPointIndex + 1) / gpsPoints.length) * 100}%, #E5E7EB ${((currentPointIndex + 1) / gpsPoints.length) * 100}%, #E5E7EB 100%)`
                  }}
                />
              </div>

              {/* Map */}
              <div className="h-96 rounded-lg overflow-hidden border border-gray-200">
                <MapContainer
                  center={center}
                  zoom={15}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  
                  {/* Complete route path (gray) */}
                  {gpsPoints.length > 1 && (
                    <Polyline 
                      positions={gpsPoints.map(p => [p.latitude, p.longitude] as [number, number])} 
                      color="#9CA3AF" 
                      weight={4}
                      opacity={0.4}
                      dashArray="5, 10"
                    />
                  )}
                  
                  {/* Traveled path (blue gradient) */}
                  {pathPoints.length > 1 && (
                    <Polyline 
                      positions={pathPoints} 
                      color="#2563EB" 
                      weight={6}
                      opacity={0.9}
                    />
                  )}
                  
                  {/* Current position marker (animated vehicle) */}
                  {currentPoint && (
                    <Marker 
                      position={[currentPoint.latitude, currentPoint.longitude]}
                      icon={L.divIcon({
                        html: `
                          <div style="
                            background: linear-gradient(45deg, #2563EB, #3B82F6);
                            width: 20px; 
                            height: 20px; 
                            border-radius: 50%; 
                            border: 3px solid white;
                            box-shadow: 0 4px 12px rgba(37, 99, 235, 0.4);
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            transform: rotate(${currentPoint.course}deg);
                            animation: pulse 2s infinite;
                          ">
                            <div style="
                              color: white;
                              font-size: 10px;
                              font-weight: bold;
                              transform: rotate(-${currentPoint.course}deg);
                            ">🚛</div>
                          </div>
                          <style>
                            @keyframes pulse {
                              0%, 100% { transform: scale(1) rotate(${currentPoint.course}deg); }
                              50% { transform: scale(1.1) rotate(${currentPoint.course}deg); }
                            }
                          </style>
                        `,
                        className: 'custom-vehicle-marker',
                        iconSize: [20, 20],
                        iconAnchor: [10, 10]
                      })}
                    >
                      <Popup>
                        <div className="p-3">
                          <h3 className="font-semibold text-blue-600">{device.deviceName}</h3>
                          <div className="mt-2 text-sm space-y-1">
                            <p><strong>Time:</strong> {new Date(currentPoint.timestamp * 1000).toLocaleString()}</p>
                            <p><strong>Speed:</strong> <span className={`font-semibold ${currentPoint.speed > 50 ? 'text-red-600' : currentPoint.speed > 20 ? 'text-yellow-600' : 'text-green-600'}`}>{currentPoint.speed} km/h</span></p>
                            <p><strong>Course:</strong> {currentPoint.course}°</p>
                            <p><strong>Coordinates:</strong> {currentPoint.latitude.toFixed(6)}, {currentPoint.longitude.toFixed(6)}</p>
                            <p className="text-xs text-gray-500 mt-2">Point {currentPointIndex + 1} of {gpsPoints.length}</p>
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  )}
                  
                  {/* Start marker (enhanced) */}
                  {gpsPoints.length > 0 && (
                    <Marker 
                      position={[gpsPoints[0].latitude, gpsPoints[0].longitude]}
                      icon={L.divIcon({
                        html: `
                          <div style="
                            background: linear-gradient(45deg, #10B981, #059669);
                            width: 24px; 
                            height: 24px; 
                            border-radius: 50%; 
                            border: 3px solid white;
                            box-shadow: 0 4px 12px rgba(16, 185, 129, 0.5);
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            animation: bounce 2s infinite;
                          ">
                            <span style="color: white; font-size: 12px; font-weight: bold;">🏁</span>
                          </div>
                          <style>
                            @keyframes bounce {
                              0%, 100% { transform: translateY(0); }
                              50% { transform: translateY(-5px); }
                            }
                          </style>
                        `,
                        className: 'custom-start-marker',
                        iconSize: [24, 24],
                        iconAnchor: [12, 12]
                      })}
                    >
                      <Popup>
                        <div className="p-3">
                          <h3 className="font-semibold text-green-600 flex items-center">
                            🏁 Journey Start
                          </h3>
                          <div className="mt-2 text-sm">
                            <p><strong>Time:</strong> {new Date(gpsPoints[0].timestamp * 1000).toLocaleString()}</p>
                            <p><strong>Location:</strong> {gpsPoints[0].latitude.toFixed(6)}, {gpsPoints[0].longitude.toFixed(6)}</p>
                            <p><strong>Initial Speed:</strong> {gpsPoints[0].speed} km/h</p>
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  )}
                  
                  {/* End marker (enhanced) */}
                  {gpsPoints.length > 1 && (
                    <Marker 
                      position={[gpsPoints[gpsPoints.length - 1].latitude, gpsPoints[gpsPoints.length - 1].longitude]}
                      icon={L.divIcon({
                        html: `
                          <div style="
                            background: linear-gradient(45deg, #DC2626, #EF4444);
                            width: 24px; 
                            height: 24px; 
                            border-radius: 50%; 
                            border: 3px solid white;
                            box-shadow: 0 4px 12px rgba(220, 38, 38, 0.5);
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            animation: glow 2s ease-in-out infinite alternate;
                          ">
                            <span style="color: white; font-size: 12px; font-weight: bold;">🏁</span>
                          </div>
                          <style>
                            @keyframes glow {
                              0% { box-shadow: 0 4px 12px rgba(220, 38, 38, 0.5); }
                              100% { box-shadow: 0 4px 20px rgba(220, 38, 38, 0.8); }
                            }
                          </style>
                        `,
                        className: 'custom-end-marker',
                        iconSize: [24, 24],
                        iconAnchor: [12, 12]
                      })}
                    >
                      <Popup>
                        <div className="p-3">
                          <h3 className="font-semibold text-red-600 flex items-center">
                            🏁 Journey End
                          </h3>
                          <div className="mt-2 text-sm">
                            <p><strong>Time:</strong> {new Date(gpsPoints[gpsPoints.length - 1].timestamp * 1000).toLocaleString()}</p>
                            <p><strong>Location:</strong> {gpsPoints[gpsPoints.length - 1].latitude.toFixed(6)}, {gpsPoints[gpsPoints.length - 1].longitude.toFixed(6)}</p>
                            <p><strong>Final Speed:</strong> {gpsPoints[gpsPoints.length - 1].speed} km/h</p>
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  )}
                </MapContainer>
              </div>

              {/* Current Point Info */}
              {currentPoint && (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-600">Timestamp</p>
                    <p className="font-medium text-sm">{new Date(currentPoint.timestamp * 1000).toLocaleString()}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-600">Speed</p>
                    <p className="font-medium text-sm">{currentPoint.speed} km/h</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-600">Course</p>
                    <p className="font-medium text-sm">{currentPoint.course}°</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-600">Coordinates</p>
                    <p className="font-medium text-sm">{currentPoint.latitude.toFixed(6)}, {currentPoint.longitude.toFixed(6)}</p>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="h-96 flex items-center justify-center bg-gray-100 rounded-lg">
              <div className="text-center">
                {playbackLoading ? (
                  <>
                    <LoadingSpinner size="lg" />
                    <p className="mt-4 text-gray-600">Loading GPS playback data...</p>
                  </>
                ) : (
                  <>
                    <div className="text-gray-400 mb-2">🕒</div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Playback Data</h3>
                    <p className="text-gray-600">
                      No GPS tracking data found for the selected time range.
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      Try adjusting the date/time range and click "Load Playback Data".
                    </p>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
          <div className="text-sm text-gray-600">
            {gpsPoints.length > 0 && (
              <span>
                Showing {gpsPoints.length} GPS points from {' '}
                {new Date(begintime * 1000).toLocaleDateString()} to {' '}
                {new Date(endtime * 1000).toLocaleDateString()}
              </span>
            )}
          </div>
          <div className="flex space-x-3">
            <Button onClick={onClose} variant="secondary">
              Close
            </Button>
            {currentPoint && (
              <Button
                onClick={() => {
                  const url = `https://www.google.com/maps?q=${currentPoint.latitude},${currentPoint.longitude}`;
                  window.open(url, '_blank');
                }}
                variant="primary"
              >
                Open in Google Maps
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehiclePlaybackModal;